import {
	createParser,
	parseAsArrayOf,
	parseAsBoolean,
	parseAsString,
} from "nuqs";

/**
 * Custom parsers for Humble's URL state management using nuqs
 * These parsers provide type-safe URL parameter handling with sensible defaults
 */

// Tab state parsers for different pages
export const festivalTabParser = parseAsString.withDefault("calendar");

// Profile tab parser for user profile pages (all/upcoming/past) - LEGACY for public profile
export const profileTabParser = createParser({
	parse: (value: string) => {
		const validTabs = ["all", "upcoming", "past"];
		return validTabs.includes(value) ? value : "all";
	},
	serialize: (value) => (value === "all" ? "" : value), // Don't include default in URL
}).withDefault("all");

// Profile content type parser (festivals/sets/shows/artists)
export const profileContentTypeParser = createParser({
	parse: (value: string) => {
		const validTypes = ["festivals", "sets", "shows", "artists"];
		return validTypes.includes(value) ? value : "festivals";
	},
	serialize: (value) => (value === "festivals" ? "" : value), // Don't include default in URL
}).withDefault("festivals");

// Profile year filter parser
export const profileYearParser = parseAsString.withDefault("all");

// Profile section parser for profile content pages (schedule/reviews/artists)
export const profileSectionParser = createParser({
	parse: (value: string) => {
		const validSections = ["schedule", "reviews", "artists"];
		return validSections.includes(value) ? value : "schedule";
	},
	serialize: (value) => (value === "schedule" ? "" : value), // Don't include default in URL
}).withDefault("schedule");

// Follow filter parser for social lists (accepted/pending/all)
type FollowFilterType = "accepted" | "pending" | "all";
export const followFilterParser = createParser({
	parse: (value: string): FollowFilterType => {
		const validFilters: readonly FollowFilterType[] = [
			"accepted",
			"pending",
			"all",
		];
		return validFilters.includes(value as FollowFilterType)
			? (value as FollowFilterType)
			: "accepted";
	},
	serialize: (value) => (value === "accepted" ? "" : value), // Don't include default in URL
}).withDefault("accepted" as FollowFilterType);

// Artist profile tab parser
export const artistTabParser = createParser({
	parse: (value: string) => {
		const validTabs = ["overview", "sets", "reviews"];
		return validTabs.includes(value) ? value : "overview";
	},
	serialize: (value) => (value === "overview" ? "" : value), // Don't include default in URL
}).withDefault("overview");

// Artists sort parser for festival pages
export const artistsSortParser = createParser({
	parse: (value: string) => {
		const validSorts = ["popularity", "alpha", "alpha-desc"];
		return validSorts.includes(value) ? value : "popularity";
	},
	serialize: (value) => (value === "popularity" ? "" : value), // Don't include default in URL
}).withDefault("popularity");

// Artist content type parser (festivals/sets/shows)
export const artistContentTypeParser = createParser({
	parse: (value: string) => {
		const validTypes = ["festivals", "sets", "shows"];
		return validTypes.includes(value) ? value : "festivals";
	},
	serialize: (value) => (value === "festivals" ? "" : value), // Don't include default in URL
}).withDefault("festivals");

// Day selection parser (empty string means "all days")
export const dayParser = parseAsString.withDefault("");

// Sort direction parser
export const sortAscendingParser = parseAsBoolean.withDefault(false);

// Review filter parser with type safety
export const reviewFilterParser = createParser({
	parse: (value: string) => {
		const validFilters = [
			"all",
			"want",
			"must",
			"saw-live",
			"saw-stream",
			"rated",
			"following",
			"loved",
		];
		return validFilters.includes(value) ? value : "all";
	},
	serialize: (value) => value,
}).withDefault("all");

// Bookmark filters parser for complex filter state
export const bookmarkFiltersParser = createParser({
	parse: (value: string) => {
		if (!value)
			return { saved: false, rated: false, following: false, stream: false };
		try {
			const parsed = JSON.parse(value);
			return {
				saved: parsed.saved === true,
				rated: parsed.rated === true,
				following: parsed.following === true,
				stream: parsed.stream === true,
			};
		} catch {
			return { saved: false, rated: false, following: false, stream: false };
		}
	},
	serialize: (value) => {
		// Only serialize if any filter is active
		if (
			!value ||
			(!value.saved && !value.rated && !value.following && !value.stream)
		)
			return "";
		return JSON.stringify(value);
	},
}).withDefault({ saved: false, rated: false, following: false, stream: false });

// MyList sort options parser
export const myListSortParser = parseAsString.withDefault("time-asc");

// Stage filter parser
export const stageParser = parseAsString.withDefault("all");

// Genre filter parser
export const genreParser = parseAsString.withDefault("all");

// Search query parser with debouncing built into nuqs
export const searchQueryParser = parseAsString.withDefault("");

// Layout preference parser
export const isHorizontalLayoutParser = parseAsBoolean.withDefault(false);

// Multiple genres parser for discovery pages
export const genresArrayParser = parseAsArrayOf(parseAsString).withDefault([]);

// Year filter parser
export const yearParser = parseAsString.withDefault("");

// View mode parser for different UI states
export const viewModeParser = parseAsString.withDefault("grid");
